import { useState } from 'react';
import { useQuery } from 'convex/react';

import { ReviewPanel, AgentEmptyState } from '../ReviewPanel';
import { CheckpointActions } from '../CheckpointActions';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';
import { useAuth } from '@/hooks/useAuth';
import type { AgentOutputRow } from '@/hooks/useJobProgress';

interface ScryComponent {
  id: string;
  type: string;
  component_kg_id?: string | null;
  category?: string;
  confidence: number;
  bbox_relative: { x: number; y: number; w: number; h: number };
  text_content_hint?: string;
  notes?: string;
}

interface ScryPagePayload {
  page_label: string;
  page_index: number;
  components: ScryComponent[];
  error?: string | null;
  error_reason?: string | null;
}

interface ScryPanelProps {
  jobId: string;
  projectId: Id<'projects'> | null;
  pages: AgentOutputRow[];
  isRunning?: boolean;
}

export function ScryPanel({ jobId, projectId, pages, isRunning }: ScryPanelProps) {
  const { token } = useAuth();
  const [activeIdx, setActiveIdx] = useState('0');

  const sketches = useQuery(
    api.sketches.listForProject,
    token && projectId ? { token, projectId } : 'skip',
  );

  if (pages.length === 0) {
    return (
      <ReviewPanel agent="scry" index={2}>
        <AgentEmptyState agent="scry" isRunning={isRunning} />
      </ReviewPanel>
    );
  }

  return (
    <ReviewPanel
      agent="scry"
      index={2}
      subtitle="Component trees"
      rightTag={`${pages.length} page${pages.length > 1 ? 's' : ''}`}
    >
      <Tabs value={activeIdx} onValueChange={setActiveIdx}>
        <TabsList className="mb-4">
          {pages.map((p, i) => {
            const payload = p.payload as ScryPagePayload;
            return (
              <TabsTrigger key={p._id} value={String(i)} className="font-mono text-[11px]">
                {payload.page_label}
              </TabsTrigger>
            );
          })}
        </TabsList>
        {pages.map((p, i) => {
          const payload = p.payload as ScryPagePayload;
          const sketchUrl = sketches?.find((s) => s.label === payload.page_label)?.url ?? null;
          return (
            <TabsContent key={p._id} value={String(i)} className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <p className="smallcaps text-graphite">
                    Sketch overlay — boxes mark each detected component
                  </p>
                  <SketchOverlay url={sketchUrl} components={payload.components} />
                  <p className="text-[11px] text-graphite/80 italic">
                    Nested boxes are expected: a container (e.g. hero section)
                    contains its children (e.g. heading, CTA).
                  </p>
                </div>
                <ComponentList payload={payload} />
              </div>
              <CheckpointActions
                jobId={jobId}
                outputId={p._id}
                agent="scry"
                itemKey={payload.page_label}
                disabled={p.reviewStatus !== 'awaiting_user'}
              />
            </TabsContent>
          );
        })}
      </Tabs>
    </ReviewPanel>
  );
}

function SketchOverlay({ url, components }: { url: string | null; components: ScryComponent[] }) {
  if (!url) {
    return (
      <div className="aspect-[4/3] rounded-sm border border-dashed border-ink/15 bg-paper-2 grid place-items-center text-graphite text-[12px]">
        Sketch unavailable
      </div>
    );
  }
  return (
    <div className="relative overflow-hidden rounded-sm border border-ink/10 bg-paper-2">
      <img src={url} alt="" className="block w-full" />
      {components.map((c) => (
        <div
          key={c.id}
          className="absolute border border-ochre/80 bg-ochre/10"
          style={{
            left: `${c.bbox_relative.x * 100}%`,
            top: `${c.bbox_relative.y * 100}%`,
            width: `${c.bbox_relative.w * 100}%`,
            height: `${c.bbox_relative.h * 100}%`,
          }}
          aria-hidden
        >
          <span className="absolute -top-4 left-0 font-mono text-[9px] text-ochre">{c.id}</span>
        </div>
      ))}
    </div>
  );
}

function ComponentList({ payload }: { payload: ScryPagePayload }) {
  if (payload.error) {
    return (
      <div className="rounded-sm border border-vermilion/40 bg-vermilion/5 p-4">
        <p className="smallcaps text-vermilion">SCRY error</p>
        <p className="mt-1 text-body text-ink">{payload.error_reason || payload.error}</p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <p className="smallcaps text-graphite">{payload.components.length} components</p>
        <p
          className="text-[11px] text-graphite/80"
          title="SCRY's self-reported confidence that it identified this component correctly."
        >
          % = model confidence
        </p>
      </div>
      <ul className="divide-y divide-ink/5">
        {payload.components.map((c) => {
          const pct = Math.round(c.confidence * 100);
          return (
            <li key={c.id} className="flex items-start gap-3 py-2">
              <span className="font-mono text-[10px] text-ochre w-6 tabular-nums">{c.id}</span>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[12px] text-ink">{c.type}</p>
                {c.component_kg_id ? (
                  <p className="font-mono text-[10px] text-graphite">{c.component_kg_id}</p>
                ) : null}
                {c.text_content_hint ? (
                  <p className="text-[12px] text-graphite italic mt-0.5">"{c.text_content_hint}"</p>
                ) : null}
              </div>
              <span
                className="font-mono text-[10px] text-graphite tabular-nums"
                title={`SCRY confidence: ${pct}%`}
              >
                {pct}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
